import { action } from '@ember/object';
import type Owner from '@ember/owner';
import { service } from '@ember/service';
import Component from '@glimmer/component';

import type { Service } from 'webapp-types';
import { TemplateObjectType } from 'webapp/enums/enums';
import { PopularLazyLoadSelector } from 'webapp/utils/common';

import makeArray from 'webapp/helpers/make-array';
import didInsert from 'webapp/modifiers/did-insert';
import didUpdate from 'webapp/modifiers/did-update';
import visibleCheckContainer, {
	containerEventName,
} from 'webapp/modifiers/visible-check-container';
import CommonListLazyLoadComponent from 'webapp/components/common/list/lazy-load';
import CommonListLoaderComponent from 'webapp/components/common/list/loader';
import FilterLineProxyComponent from 'webapp/components/filter/line-proxy';
import FilterNoResultsComponent from 'webapp/components/filter/no-results';
import TemplatesGridItemComponent from 'webapp/components/templates/grid-item';

import FilterSortPublicTemplate from 'webapp/models-custom/filter-sort/public-template';
import type Template from 'webapp/models-custom/template';

interface Args {
	model: Template[];
	useTemplate: (model: Template) => void;
	previewTemplate: (model: Template) => void;
}

export default class TemplatesPublicGridComponent extends Component<Args> {
	@service('model-manager/templates') declare mmTemplatesService: Service.ModelManager.Templates;
	@service('model-manager/source-types')
	declare mmSourceTypesService: Service.ModelManager.SourceTypes;
	@service('account') declare accountService: Service.Account;

	filterSort: FilterSortPublicTemplate;
	visibleCheckContainerNode: HTMLElement | null = null;
	maxItemsInRow = 7;
	recommendedTemplates: Template[] = [];

	constructor(owner: Owner, args: Args) {
		super(owner, args);

		if (args.model[0]?.objectType === TemplateObjectType.BOARD) {
			this.recommendedTemplates =
				this.mmTemplatesService.getRecommendedBoardPublicTemplateModels(14);
		}

		const filterSort = new FilterSortPublicTemplate({
			selectors: PopularLazyLoadSelector.grid,
			getAllItems: () => {
				return this.args.model;
			},
			afterFilter: () => {
				// for visible-check
				const node = this.visibleCheckContainerNode;
				if (node) {
					this.triggerContentChange(node);
				}
			},
		});

		this.filterSort = filterSort;
	}

	get isEmpty(): boolean {
		return this.filterSort.noResults;
	}

	get isSearching(): boolean {
		return !!this.filterSort.typedFilter;
	}

	get showRecommended(): boolean {
		return this.recommendedTemplates.length > 0 && !this.isSearching;
	}

	@action
	setupView(node: HTMLElement): void {
		this.visibleCheckContainerNode = node;
	}

	@action
	triggerContentChange(node: HTMLElement): void {
		// for visible-check
		node.dispatchEvent(new Event(containerEventName));
	}

	// filter functions
	recommendedTemplatesFilterFn = (items: Template[]) => {
		const filteredRecommended = this.recommendedTemplates.filter((template) =>
			items.find((item) => item.id === template.id),
		);
		return filteredRecommended;
	};

	otherTemplatesFilterFn = (items: Template[]) => {
		// when searching, show all items
		// otherwise, filter out recommended templates

		if (this.isSearching) {
			return items;
		}

		return items.filter((t) => !this.recommendedTemplates.find((item) => item.id === t.id));
	};
	<template>
		<FilterLineProxyComponent @model={{this.filterSort}} />

		{{#if this.isEmpty}}
			<FilterNoResultsComponent @model={{this.filterSort}} />
		{{/if}}

		{{! Recommended templates section - only shown when not searching }}
		{{#if this.showRecommended}}
			<CommonListLazyLoadComponent
				@classNames="lazy-grid"
				@filterSort={{this.filterSort}}
				@filterExtraFn={{this.recommendedTemplatesFilterFn}}
				@initialRowCount={{10}}
				@loadMoreRowCount={{5}}
				@animationProperty="id"
				@autoLoaded={{true}}
				@bottomDistance={{700}}
				as |visibleItems exporter|
			>
				<ul
					class="flex-ct flex-ct--size-board recommended-template-list"
					data-hidden={{if this.filterSort.noResults "true" "false"}}
					{{visibleCheckContainer}}
				>
					{{#each visibleItems as |item|}}
						<TemplatesGridItemComponent
							@model={{item}}
							@onPreviewTemplate={{@previewTemplate}}
							@onUseTemplate={{@useTemplate}}
							@isVisibleOnScreen={{true}}
						/>
					{{/each}}

					{{! adding (max-in-line - 1) fake items so the incomplete last row items are not stretched too much }}
					{{#each (makeArray length=exporter.itemsPerRow)}}
						<li class="flex-item flex-item--fake"></li>
					{{/each}}
				</ul>
				{{#unless exporter.areAllItemsLoaded}}
					<CommonListLoaderComponent />
				{{/unless}}
			</CommonListLazyLoadComponent>
		{{/if}}

		{{! Other/All templates section }}
		<CommonListLazyLoadComponent
			@classNames="lazy-grid"
			@filterSort={{this.filterSort}}
			@filterExtraFn={{this.otherTemplatesFilterFn}}
			@initialRowCount={{10}}
			@loadMoreRowCount={{5}}
			@animationProperty="id"
			@autoLoaded={{true}}
			@bottomDistance={{700}}
			as |visibleItems exporter|
		>
			<ul
				class="flex-ct flex-ct--size-board other-template-list"
				data-hidden={{if this.filterSort.noResults "true" "false"}}
				{{didInsert this.setupView}}
				{{didUpdate this.triggerContentChange visibleItems}}
				{{visibleCheckContainer}}
			>
				{{#each visibleItems as |item|}}
					<TemplatesGridItemComponent
						@model={{item}}
						@onPreviewTemplate={{@previewTemplate}}
						@onUseTemplate={{@useTemplate}}
					/>
				{{/each}}

				{{! adding (max-in-line - 1) fake items so the incomplete last row items are not stretched too much }}
				{{#each (makeArray length=exporter.itemsPerRow)}}
					<li class="flex-item flex-item--fake"></li>
				{{/each}}
			</ul>
			{{#unless exporter.areAllItemsLoaded}}
				<CommonListLoaderComponent />
			{{/unless}}
		</CommonListLazyLoadComponent>
	</template>
}
